/**
 * Frontend-local node registry for the first canvas slice.
 *
 * The Phase 3 design calls for a SERVER-DRIVEN node registry (delivered with
 * the node-contract ADR). For this slice we hardcode the three node types
 * needed to build and test a trigger on the canvas — Manual trigger, Set
 * (constant), Display — so the canvas + save loop can be exercised without the
 * engine. When the server-driven registry lands, this file is replaced by a
 * fetch of the backend registry.
 */

export type NodeCategory =
  | "trigger"
  | "source"
  | "transform"
  | "data-in"
  | "flow"
  | "validate"
  | "display"
  | "target"
  /** Canvas annotations — no ports, never executed, never validated. */
  | "note";

/** One output port of a node that has more than one.
 *
 *  Most nodes have a single unlabelled output, and an edge from them carries no
 *  port. A node that splits its rows — Validate, so far — declares its ports
 *  here, and the canvas renders one labelled handle each. The label is not
 *  decoration: "valid" and "invalid" leaving the same box is exactly the thing
 *  a reader must not have to guess. */
export interface NodeOutputPort {
  id: string;
  label: string;
  /** Mantine colour for the handle's label, so the happy path reads as such. */
  color?: string;
}

export interface NodeConfigField {
  key: string;
  label: string;
  placeholder?: string;
  type?: "text" | "number" | "select";
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
}

export interface NodeTypeDef {
  kind: string;
  label: string;
  category: NodeCategory;
  description: string;
  configFields: NodeConfigField[];
  defaultConfig: Record<string, unknown>;
  /** Declared only by nodes with more than one output. */
  outputs?: NodeOutputPort[];
}

export const NODE_TYPES: NodeTypeDef[] = [
  {
    kind: "trigger.manual",
    label: "Manual Trigger",
    category: "trigger",
    description: "Starts the pipeline when an authorized user clicks Run.",
    configFields: [],
    defaultConfig: {},
  },
  {
    kind: "trigger.schedule",
    label: "Schedule",
    category: "trigger",
    description:
      "Runs the pipeline on a fixed interval. Press Run to start the schedule and Stop to pause it — the ring shows the countdown to the next run.",
    configFields: [
      { key: "intervalValue", label: "Run every", type: "number", min: 1 },
      {
        key: "intervalUnit",
        label: "Unit",
        type: "select",
        options: [
          { value: "seconds", label: "Seconds" },
          { value: "minutes", label: "Minutes" },
          { value: "hours", label: "Hours" },
          { value: "days", label: "Days" },
        ],
      },
    ],
    defaultConfig: { intervalValue: 1, intervalUnit: "hours" },
  },
  {
    kind: "trigger.blob",
    label: "Blob arrival",
    category: "trigger",
    // Configured via a custom inspector (BlobTriggerConfig): pick one of your
    // Azure Blob connections, then browse to the folder to watch. No free-text.
    description:
      "Runs when a new file lands in a watched Azure Blob folder. Pick a connection and browse to the folder to watch, then press Run to arm it (Stop to disarm).",
    configFields: [],
    defaultConfig: { connectionId: "", connectionName: "", container: "", prefix: "" },
  },
  {
    kind: "trigger.s3",
    label: "S3 arrival",
    category: "trigger",
    // Same watch as `trigger.blob` against a bucket — they share one
    // pipeline_blob_watch row server-side (ADR-0021).
    description:
      "Runs when a new object lands in a watched S3 prefix. Pick a connection and browse to the folder to watch, then press Run to arm it (Stop to disarm).",
    configFields: [],
    defaultConfig: { connectionId: "", connectionName: "", bucket: "", prefix: "" },
  },
  {
    kind: "veeva.query",
    label: "Veeva Source",
    category: "source",
    // Configured via a custom inspector (VeevaQueryConfig): pick a Veeva
    // connection, build/edit VQL, Test to preview the table.
    description:
      'Reads data from a Veeva Vault with VQL. Pick a connection, build the query, and Test to preview the table. Reference fields come back as ids; tick "also fetch labels" to get a <field>__label column beside each one. Outputs rows — saving documents, renditions, attachments or audit trails is the Save to Blob target\'s job.',
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      vql: "",
      // Off: a reference column holds an id, and an id is what a load back into
      // Vault needs. Labels are added beside it, never instead of it.
      resolveLabels: false,
    },
  },
  {
    kind: "blob.source",
    label: "Blob Source",
    category: "source",
    // Configured via a custom inspector (BlobSourceConfig.tsx): a SQL query
    // over the container listing (default), or one tabular data file.
    description:
      "Selects documents from Azure Blob storage with SQL. The container is listed as the table blobs — one row per file (name, path, folder, extension, size, dates, metadata) — and your SELECT picks the files. Outputs one row per matched file with a _file reference downstream nodes fetch it by, e.g. Load to Veeva creating documents. Can also read one CSV/TSV/JSON/XLSX data file into rows.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      container: "",
      // A new node is a document list. A node saved before it existed has no
      // `mode`, a `path` and no query, and keeps reading that one file.
      mode: "documents",
      prefix: "",
      query: "SELECT * FROM blobs ORDER BY path",
      path: "",
      sheet: "",
      delimiter: "",
    },
  },
  {
    kind: "database.source",
    label: "Database Source",
    category: "source",
    // Configured via a custom inspector (DatabaseSourceConfig): pick a
    // database connection, then a schema and table — or write a SELECT — and
    // Test to preview the rows.
    description:
      "Reads a table out of a connected database into rows — PostgreSQL, SQL Server, Oracle or MySQL. Pick a connection, then a schema and table, or write your own SELECT. Test previews the rows using the same read a run does. Reads only: anything that is not a single SELECT is refused before it reaches the database.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      // "table" | "query" — a picked table, or a SELECT typed by hand.
      mode: "table",
      schema: "",
      table: "",
      query: "",
    },
  },
  {
    kind: "s3.source",
    label: "S3 Source",
    category: "source",
    // Configured via a custom inspector (S3SourceConfig): pick an S3
    // connection, browse to an object, Test to preview the parsed table.
    description:
      "Reads a tabular object from AWS S3 into rows — CSV, TSV, JSON, JSONL, or Excel (.xlsx). Pick a connection, choose the object, and Test to preview the table. Outputs the rows plus the source address.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      bucket: "",
      path: "",
      sheet: "",
      delimiter: "",
    },
  },
  {
    kind: "cara.source",
    label: "CARA Source",
    category: "source",
    // Configured via a custom inspector (CaraSourceConfig): pick a CARA
    // connection, pick the object type, columns and filters — or write the
    // search body — then Test to preview the table.
    description:
      "Reads records out of a Generis CARA tenant. CARA has no query language, so you pick an object type and its columns rather than writing a query (the Search body tab is the escape hatch for anything the picker cannot express). Records are sparse, so picking columns is what makes the table the same shape every run. Read-only: nothing in the platform can write to CARA.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      mode: "builder",
      typeName: "",
      fields: [],
      filters: [],
      text: "",
      searchInContent: false,
      body: "",
      // A default, not a limit: a CARA view holds six figures of records, and
      // a run that quietly pulled all of them would be a surprise.
      maxRows: 1000,
      bucket: "",
      path: "",
      sheet: "",
      delimiter: "",
    },
  },
  {
    kind: "transform.convert_type",
    label: "Convert Type",
    category: "transform",
    description:
      "Convert chosen columns to a real type — number, integer, boolean, or date (ISO). Source data is text; this is where you opt into types. Values that don't convert become empty (or keep the original).",
    configFields: [],
    defaultConfig: { columns: [], to: "number", onError: "null" },
  },
  {
    kind: "transform.rename",
    label: "Rename Columns",
    category: "transform",
    description: "Rename columns — add one row per column you want to rename (from → to).",
    configFields: [],
    defaultConfig: { renames: [] },
  },
  {
    kind: "transform.select",
    label: "Select Columns",
    category: "transform",
    description: "Keep only the columns you pick, or drop them. Everything else passes through.",
    configFields: [],
    defaultConfig: { mode: "keep", columns: [] },
  },
  {
    kind: "transform.trim",
    label: "Trim Whitespace",
    category: "transform",
    description:
      "Clean up text cells (all text columns if none picked): strip leading/trailing spaces, optionally collapse internal runs of whitespace, and optionally change case (lower / UPPER / Title).",
    configFields: [],
    defaultConfig: { columns: [], collapse: false, case: "none" },
  },
  {
    kind: "transform.fill_null",
    label: "Fill Missing",
    category: "transform",
    description:
      "Replace empty/missing cells with a value, in chosen columns (all columns if none picked).",
    configFields: [],
    defaultConfig: { columns: [], value: "", treatBlank: true },
  },
  {
    kind: "transform.filter",
    label: "Filter Rows",
    category: "transform",
    description: "Keep only rows where a column meets a condition.",
    configFields: [],
    defaultConfig: { column: "", op: "not_empty", value: "" },
  },
  {
    kind: "transform.replace",
    label: "Replace Values",
    category: "transform",
    description:
      "In one column, replace a cell with a new value when it equals — or contains — a given value. Whole-cell replace.",
    configFields: [],
    defaultConfig: { column: "", match: "equals", find: "", replace: "" },
  },
  {
    kind: "transform.sql",
    label: "SQL Query",
    category: "transform",
    description:
      "Run a SQL SELECT over the input rows. The incoming table is named `input` — e.g. SELECT * FROM input WHERE ... Full SQL (WHERE, JOIN, GROUP BY, aggregates); runs in an isolated in-memory database.",
    configFields: [],
    defaultConfig: { query: "SELECT * FROM input" },
  },
  {
    kind: "transform.join",
    label: "Join",
    category: "transform",
    description:
      "Relational join of two tables on a key column (like a VLOOKUP / SQL JOIN). Wire the left table to the top input and the right to the bottom. Combines columns side-by-side.",
    configFields: [],
    defaultConfig: { leftKey: "", rightKey: "", how: "inner", suffix: "_2" },
  },
  {
    kind: "transform.union",
    label: "Append (Union)",
    category: "transform",
    description:
      "Stack the rows of two tables that share columns (like SQL UNION). Wire both tables to the two inputs.",
    configFields: [],
    defaultConfig: { mode: "union" },
  },
  {
    kind: "transform.dedupe",
    label: "Remove Duplicates",
    category: "transform",
    description:
      "Drop duplicate rows. Pick the key columns (or leave empty to match on the whole row), and keep the first or last occurrence.",
    configFields: [],
    defaultConfig: { columns: [], keep: "first" },
  },
  {
    kind: "transform.split_column",
    label: "Split Column",
    category: "transform",
    description:
      "Split one text column into several by a separator — e.g. a full name on a space into first / last. Extra pieces are dropped; missing ones fill empty.",
    configFields: [],
    defaultConfig: { source: "", separator: " ", into: [], keepSource: false },
  },
  {
    kind: "transform.merge_columns",
    label: "Merge Columns",
    category: "transform",
    description:
      "Combine several columns into one, joined by a separator — the inverse of Split Column. Optionally keep the source columns.",
    configFields: [],
    defaultConfig: { columns: [], target: "", separator: " ", keepSources: true },
  },
  {
    kind: "transform.date_format",
    label: "Reformat Date",
    category: "transform",
    description:
      "Parse a column as a date and write it back in a chosen format (e.g. 05-Jan-2026). Use this when a target system needs an exact date string.",
    configFields: [],
    defaultConfig: { column: "", format: "%Y-%m-%d", target: "", onError: "keep" },
  },
  {
    kind: "transform.conditional",
    label: "Conditional (CASE)",
    category: "transform",
    description:
      "Write a column based on rules — the first matching case wins (like SQL CASE / an if-else). e.g. if status equals A → 'Active', else 'Inactive'.",
    configFields: [],
    defaultConfig: { target: "", cases: [], default: "" },
  },
  {
    kind: "transform.unpivot",
    label: "Unpivot (melt)",
    category: "transform",
    description:
      "Reshape wide → long: keep the id columns and turn each chosen value column into its own row (a name column + a value column).",
    configFields: [],
    defaultConfig: {
      idColumns: [],
      valueColumns: [],
      varName: "variable",
      valueName: "value",
    },
  },
  {
    kind: "transform.explode",
    label: "Explode",
    category: "transform",
    description:
      "Turn one row with a multi-value cell into many rows — one per value. A list cell explodes element-wise; a text cell splits on a separator.",
    configFields: [],
    defaultConfig: { column: "", separator: "," },
  },
  {
    kind: "ai.predict_metadata",
    label: "Metadata enrichment (AI)",
    category: "transform",
    // Custom inspector (PredictMetadataConfig): a fields table, the model
    // picker, limits, and a Test preview over the first three documents.
    description:
      "Reads each upstream document (a Blob Source document list, or Veeva document rows) and asks an AI model to predict the fields you define — one column per field, plus its confidence and a quote from the document as evidence. Advisory: the values must pass an Approval node before any node writes them to Vault.",
    configFields: [],
    defaultConfig: {
      fields: [{ name: "", prompt: "", examples: "", type: "text", values: "", required: false }],
      connectionId: "platform",
      deployment: "",
      maxDocuments: 200,
      maxChars: 20000,
      concurrency: 4,
    },
  },
  {
    kind: "data.set",
    label: "Set (Constant)",
    category: "data-in",
    // A typed constant (number / text / boolean / validated JSON) — see the
    // custom inspector in PipelineCanvas. No generic config fields.
    description: "Emits a constant value into the flow (number, text, boolean, or JSON).",
    configFields: [],
    defaultConfig: { valueType: "number", value: 0 },
  },
  {
    kind: "data.table",
    label: "Create Table",
    category: "data-in",
    // A hand-built dataframe — a small grid editor (columns + rows) in the
    // custom inspector (TableConfig). Outputs rows just like a source.
    description:
      "Build a table by hand — define columns and type in the rows. Outputs the rows like a source would. Values are text; add a Convert Type node to set real types.",
    configFields: [],
    defaultConfig: { columns: ["column_1", "column_2"], rows: [["", ""]] },
  },
  {
    kind: "flow.run_pipeline",
    label: "Run Pipeline",
    category: "flow",
    // Custom inspector (RunPipelineConfig): pick a pipeline in this project.
    description:
      "Runs another pipeline in this project and waits for it to finish. This is how a data migration is built: one ETL pipeline per object — products, then studies, then their documents — wired in the order their references demand. The child is a run of its own, with its own record and history; this node passes on its outcome, and by default the migration stops if the child fails.",
    configFields: [],
    defaultConfig: { pipelineId: "", pipelineName: "", continueOnFailure: false },
  },
  {
    kind: "flow.undo",
    label: "Undo a load",
    category: "flow",
    // Custom inspector (UndoConfig): pick a recorded load, pick how, say why.
    description:
      "Takes back what a previous load put into a vault — the exact records it created, by id. It never runs a query, never touches records it only updated (reversing those would need a before-image the platform does not keep), and a load can be undone once. Inactivating is the default and keeps the record, its audit trail and anything referencing it; deleting is available and is exactly what it says. In Validation and Production the run stops for an Administrator's approval first, and that gate cannot be switched off.",
    configFields: [],
    defaultConfig: { reportId: "", mode: "inactivate", reason: "" },
  },
  {
    kind: "validate.rows",
    label: "Validate",
    category: "validate",
    // Custom inspector (ValidateConfig): rules by hand, or read from the target
    // object's own field metadata.
    description:
      "Checks rows before they are loaded and splits them in two: the ones the target will accept, and the ones it will not. Invalid rows are not dropped — they come out of the second port carrying the row number and every reason, so you can save them as the fix-list a data owner works from. Point it at a Veeva object and it reads the rules from the vault itself: required fields, field lengths, picklist values, unique keys.",
    configFields: [],
    defaultConfig: {
      mode: "rules",
      rules: [],
      failIfInvalid: false,
      connectionId: "",
      object: "",
      refuseUnknownColumns: true,
      // Off by default: Vault marks fields `required` that it fills itself
      // (status__v on product__v), and its metadata gives no way to tell those
      // apart — so defaulting this on would block good loads.
      refuseMissingRequired: false,
    },
    outputs: [
      { id: "out-0", label: "valid", color: "teal" },
      { id: "out-1", label: "invalid", color: "orange" },
    ],
  },
  {
    kind: "veeva.lookup",
    label: "Look up in Veeva",
    category: "transform",
    // Custom inspector (LookupConfig): what to match on, and what to bring back.
    description:
      "Finds each row's matching record in the vault and brings back its id and any fields you name. What a migration needs to load anything that references something else: a study row arrives carrying a product's name or its source id, and Vault wants the product's record id. Two outputs — the rows that resolved, and the rows that found nothing, which are work rather than an error.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      object: "",
      column: "",
      matchField: "",
      fields: [],
      prefix: "lookup_",
      failIfMissing: false,
    },
    outputs: [
      { id: "out-0", label: "found", color: "teal" },
      { id: "out-1", label: "not found", color: "orange" },
    ],
  },
  {
    kind: "validate.profile",
    label: "Profile",
    category: "validate",
    description:
      "Describes what is actually in the data: per column, how many rows are empty, how many distinct values there are, how long the longest one is, and a few examples. The first thing to do with a source nobody has looked at in ten years — and what tells you which column can carry the upsert key, and whether a column is full enough to be worth mapping at all.",
    configFields: [],
    defaultConfig: {},
  },
  {
    kind: "validate.compare",
    label: "Compare",
    category: "validate",
    // Custom inspector (CompareConfig): the key, and which fields to compare.
    description:
      "Matches two datasets on a key and reports how they disagree, on three separate outputs: missing (on the left, not the right), different (same key, different values) and extra (on the right, not the left). Three outputs because those are three different pieces of work for whoever has to fix them.",
    configFields: [],
    defaultConfig: { key: "", rightKey: "", fields: [] },
    outputs: [
      { id: "out-0", label: "missing", color: "orange" },
      { id: "out-1", label: "different", color: "yellow" },
      { id: "out-2", label: "extra", color: "grape" },
    ],
  },
  {
    kind: "validate.reconcile",
    label: "Reconcile",
    category: "validate",
    // Custom inspector (ReconcileConfig): connection, object, key, fields.
    description:
      "Asks the vault what it actually holds now, by key, and compares it with the rows coming in. The question every migration ends on — did it land? — answered against the live system rather than against a report written at load time. Reads only. Same three outputs as Compare.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      object: "",
      key: "",
      targetKey: "",
      fields: [],
    },
    outputs: [
      { id: "out-0", label: "missing", color: "orange" },
      { id: "out-1", label: "different", color: "yellow" },
      { id: "out-2", label: "extra", color: "grape" },
    ],
  },
  {
    kind: "flow.approval",
    label: "Approval",
    category: "flow",
    // Custom inspector (ApprovalConfig): the question, and who will be asked —
    // which follows the pipeline's lifecycle state, so the inspector says it.
    description:
      'Stops the run until a person approves it. The gate a regulated migration needs — "the products loaded; shall we load the studies?" — asked at the moment it matters. In Development any Developer may decide; in Validation and Production it takes an Administrator, and not the person who started the run. Rows pass through unchanged; a rejection stops the run and records why. Waiting is free: the run holds no worker while it waits.',
    configFields: [],
    defaultConfig: { title: "", instructions: "" },
  },
  {
    kind: "display.json",
    label: "View dataframe",
    category: "display",
    description:
      "Shows incoming data as a table or JSON when the pipeline runs. Each input has its own port and produces its own result.",
    configFields: [{ key: "inputs", label: "Number of inputs", type: "number", min: 1, max: 10 }],
    defaultConfig: { inputs: 1 },
  },
  {
    kind: "blob.sink",
    label: "Save to Blob",
    category: "target",
    // Configured via a custom inspector (BlobSinkConfig): what kind of data is
    // being saved, the connection, the container (pick or create), then either
    // a file name (table) or which per-record artefacts to fetch (Veeva).
    description:
      "Save the incoming data to Azure Blob storage. Choose what you are saving: a table (one file), Veeva documents, or Veeva object records. For Veeva data each row becomes its own folder holding that record's file, viewable rendition, attachments and audit trail — whichever you switch on.",
    configFields: [],
    defaultConfig: {
      dataKind: "table",
      connectionId: "",
      connectionName: "",
      container: "",
      prefix: "",
      format: "csv",
      delimiter: "",
      filename: "",
      // Veeva kinds: how to find and name each record, and which artefacts to
      // save. Neither the vault nor the object appears here — both come from
      // the Veeva Source that read the rows, and the inspector never asks.
      idColumn: "id",
      folderNameColumns: ["id"],
      saveDocument: true,
      saveRendition: false,
      saveRecordTable: true,
      recordFormat: "csv",
      saveRecordPdf: false,
      pdfColumns: [],
      saveAttachments: false,
      saveAuditTrail: false,
      // Once per run, beside the record folders: the query's own result set.
      saveResultTable: false,
      resultFormat: "csv",
      resultFilename: "results",
    },
  },
  // --- Archive module -------------------------------------------------------
  {
    kind: "archive.source",
    label: "Read from archive",
    category: "source",
    // The inverse of archive.sink, and the node scheduled reporting,
    // reconciliation and archive-to-archive migration all hang off.
    description:
      "Reads an archive back into the pipeline: the catalogue of a document archive, or the rows of a retired system's table or saved record view. Metadata and rows only — archived files are never streamed out through a pipeline, because retrieval is gated on a grant and audited per object in the UI.",
    configFields: [],
    defaultConfig: {
      containerId: "",
      containerName: "",
      datasetName: "",
      recordViewId: "",
      filters: [],
      limit: 5000,
    },
  },
  {
    kind: "archive.disposition",
    label: "Disposition review",
    category: "source",
    // Retention is reviewed periodically (CLAUDE.md §9.3). A review is
    // scheduled, record-producing work — which is what a pipeline is for.
    description:
      "Reports which records have reached their retention date, and which are held. Deletes nothing: a record falling due means somebody must decide, and in a GxP system that decision is a human's. The run itself is the evidence that the review happened.",
    configFields: [],
    defaultConfig: { containerId: "", containerName: "", withinDays: 0 },
  },
  {
    kind: "archive.verify",
    label: "Verify checksums",
    category: "source",
    description:
      "Re-reads archived objects from storage and recomputes their checksums. The re-read is the point — comparing a stored checksum against itself proves nothing. Sample a subset for a routine run, or leave it empty to check every object.",
    configFields: [],
    defaultConfig: { containerId: "", containerName: "", sampleSize: 100 },
  },
  {
    kind: "archive.ensure_container",
    label: "Ensure archive",
    category: "target",
    // Provisions the archival container the pipeline writes into, so an
    // archiving flow is self-contained. Idempotent: a scheduled pipeline must
    // not fail on its second tick, and must not create a second archive.
    description:
      "Creates the Archival Container this pipeline archives into, or reuses it if the code already exists. Wire it between the document source and Archive to container so the destination comes from the pipeline rather than being picked by hand; the documents pass straight through. Creating an archive needs the Administrator role — a Developer can archive into an existing one.",
    configFields: [],
    defaultConfig: {
      code: "",
      name: "",
      cluster: "C",
      kind: "documents",
      sourceSystem: "",
      retentionClass: "10y",
      storageClass: "STANDARD",
      objectLockMode: "",
      onExisting: "use",
      fields: [],
    },
  },
  {
    kind: "archive.sink",
    label: "Archive to container",
    category: "target",
    // Archiving is a target like any other, so it inherits versioning, runs,
    // triggers and the run-detail UI unchanged. Configured via a custom
    // inspector: pick the archival container, map source metadata onto its
    // model, and choose the retention class for this intake.
    description:
      "Archives documents into an Archival Container — downloaded from the vault the upstream Veeva Source read, checksummed, mapped onto the container's metadata model, and written with retention applied. Wire a Veeva Source that reads FROM documents (selecting id) into it. Its output is the per-object archive report plus the verification result; connect it to a View dataframe node to read it. Re-running skips objects already archived.",
    configFields: [],
    defaultConfig: {
      containerId: "",
      containerName: "",
      onDuplicate: "skip",
      retentionClass: "",
      objectType: "document",
      mappings: [],
    },
  },
  {
    kind: "archive.dataset_sink",
    label: "Archive dataset",
    category: "target",
    // Cluster A — preserving a retired application's tables so they stay
    // queryable after the system is switched off.
    description:
      "Preserves the incoming rows as a table of a retired system: writes the extract and a manifest (row count, column schema, checksum), and materialises the rows so they stay queryable after the source application is gone.",
    configFields: [],
    defaultConfig: {
      containerId: "",
      containerName: "",
      datasetName: "",
      description: "",
    },
  },
  {
    kind: "db.snapshot",
    label: "Archive database",
    category: "target",
    // Cluster A, the whole-application case. `archive.dataset_sink` archives
    // one table from one rows input; a retired system has hundreds, so the
    // fan-out lives inside this node rather than on the canvas.
    description:
      "Archives many of a retired application's tables in one run. Choose a schema and it preserves every table in it — each with its own manifest, checksum, column schema, primary key and foreign keys — so the system stays queryable and rejoinable after it is switched off. Leave the table list empty to take the whole schema.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      containerId: "",
      containerName: "",
      schema: "",
      tables: [],
      includeViews: false,
      namePrefix: "",
    },
  },
  {
    kind: "s3.sink",
    label: "Save to S3",
    category: "target",
    // Configured via a custom inspector (S3SinkConfig): connection, format,
    // bucket, folder browse, and a free-text filename. No Veeva data kinds —
    // per-record document archives land in the platform's own Blob storage.
    description:
      "Save the incoming rows to AWS S3 as one file — CSV, TSV, JSON, JSONL, or Excel (.xlsx). Pick a connection and format, browse to a folder, and name the file. A re-run overwrites the object at the same key.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      bucket: "",
      prefix: "",
      format: "csv",
      delimiter: "",
      filename: "",
    },
  },
  {
    kind: "veeva.sink",
    label: "Load to Veeva",
    category: "target",
    // Configured via a custom inspector (VeevaSinkConfig): data kind,
    // connection, object, and column → Veeva-field mapping. Unusually for a
    // target, it has an output port: the vault's per-row load report (connect
    // it to a Display).
    description:
      "Write rows back to a Veeva Vault (one record per row). Choose what you are loading — object records or documents — then a connection, and map columns → Veeva fields. Object records can be created or updated; documents too — map file to each document's File Staging path (a relative path resolves under the connection's own root), or point the node at Blob storage and it copies the files to staging first. Leave the file column unmapped for metadata-only placeholders. Update changes only the fields you map and needs the Veeva id on every row. Its output is the vault's load report — a row per input row with SUCCESS/FAILURE, the id, and any error — plus the job id. Connect it to a View dataframe node to see the report.",
    configFields: [],
    defaultConfig: {
      // Object records by default: a graph saved before documents existed
      // carries no `dataKind`, and must keep doing what it did.
      dataKind: "veeva_object",
      connectionId: "",
      connectionName: "",
      object: "",
      action: "create",
      mappings: [],
      migrationMode: false,
      // Updating documents: classify them (out of the Inbox) in the same call
      // as the rest of their metadata; lifecycle from lifecycle__v, this, or
      // the type's only lifecycle.
      reclassify: false,
      reclassifyLifecycle: "",
      // Documents: after the load, move each accepted document to this status
      // through its lifecycle's state-change user action.
      updateStatus: false,
      targetStatus: "In Progress",
      // Creating documents only. `staging` means the `file` column already
      // holds File Staging paths — the shape once the files are there, put by
      // the Load to Veeva FTP node or by someone else. `blob` copies them
      // first, from the connection and container below.
      fileSource: "staging",
      fileConnectionId: "",
      fileContainer: "",
      // Creating documents: Vault requires a type and a lifecycle on every row,
      // and a lifecycle is not implied by the type. Chosen once for the load —
      // the *Name* keys drive the pickers, the others are the labels Vault gets.
      documentType: "",
      documentTypeName: "",
      documentSubtype: "",
      documentSubtypeName: "",
      documentClassification: "",
      documentClassificationName: "",
      documentLifecycle: "",
    },
  },
  {
    kind: "veeva.staging",
    label: "Load to Veeva FTP",
    category: "target",
    // Custom inspector (VeevaStagingSinkConfig). A target that also has an
    // output port: its report *is* the table the document loader needs next,
    // one row per file with the staging path in a column called `file`.
    description:
      "Copy a folder of files from Blob storage into Vault File Staging (the FTP area), under a path you choose. Vault's bulk document APIs take a manifest of staging paths, so the bytes have to be there first — and often they already are, put there by someone else. Splitting this out means the two halves run apart: stage today, create the documents tomorrow, or create documents against files a colleague staged last week. Its output is one row per file — staging path, original Blob path, size, status — ready to wire into Load to Veeva or a View dataframe.",
    configFields: [],
    defaultConfig: {
      connectionId: "",
      connectionName: "",
      // Relative to the vault user's own staging root, which is the only place
      // they can write; the node resolves it at run time.
      path: "",
      fileConnectionId: "",
      fileContainer: "",
      filePrefix: "",
      overwrite: true,
    },
  },
  {
    kind: "note.text",
    label: "Text",
    category: "note",
    description:
      "A note on the canvas. It has no inputs or outputs and never runs — double-click it to write, drag a corner to resize.",
    configFields: [],
    defaultConfig: { text: "", color: "yellow", width: 220, height: 120 },
  },
  {
    kind: "note.header",
    label: "Text with header",
    category: "note",
    description:
      "A note with a bold header and a body. It has no inputs or outputs and never runs — double-click it to write, drag a corner to resize.",
    configFields: [],
    defaultConfig: { title: "", text: "", color: "yellow", width: 240, height: 150 },
  },
];

/** A canvas annotation: drawn, saved, never part of what runs. */
export function isNoteKind(kind: string): boolean {
  return kind.startsWith("note.");
}

/** The sticky-note backgrounds — light, so they sit on the light brand canvas
 *  without competing with the category accents of real nodes. */
export const NOTE_COLORS: { value: string; label: string; bg: string }[] = [
  { value: "yellow", label: "Yellow", bg: "#FFF8D6" },
  { value: "blue", label: "Blue", bg: "#E6F0F7" },
  { value: "pink", label: "Pink", bg: "#FCE8EF" },
  { value: "grey", label: "Grey", bg: "#E5E5E5" },
];

export function noteBackground(color: unknown): string {
  return (NOTE_COLORS.find((c) => c.value === color) ?? NOTE_COLORS[0]!).bg;
}

export const NODE_BY_KIND: Map<string, NodeTypeDef> = new Map(NODE_TYPES.map((n) => [n.kind, n]));

/** Palette grouping (Simulink-style): category display names + order. */
export const CATEGORY_LABEL: Record<NodeCategory, string> = {
  trigger: "Triggers",
  source: "Sources",
  transform: "Transform",
  "data-in": "Data",
  flow: "Flow",
  validate: "Validate",
  display: "Display",
  target: "Targets",
  note: "Notes",
};

/**
 * One accent per category — the colour a node wears on the canvas and in the
 * palette. Assignment is by **category, never by vendor**: a Veeva source and a
 * Blob source are both "source" coloured, because on a canvas the useful
 * question is *what kind of step is this*, not *whose system is it*. Vendor
 * identity is carried by the logo on the connection card
 * (`features/integration/sourceVisuals.ts`).
 *
 * The mnemonic: **warm means side effects.** Target is the only warm colour,
 * and targets are the only nodes that write to the outside world.
 *
 * Transform is teal because it is 17 of the 27 nodes — the workhorse should
 * recede, and the rare steps should be the ones that catch the eye. None of
 * them collides with the brand blue (214°) or with the status set in
 * `shared/ui/palette.ts`.
 *
 * Hues were ≥33° apart with six categories. **Flow (indigo, 230°) sits 25° from
 * trigger (violet, 255°)** — recorded rather than glossed over: the remaining
 * gaps in the scale are the brand blue and the status colours, and taking one of
 * those would be worse than two neighbouring hues in a palette where the label
 * is always visible beside the colour.
 * Transform is teal because it is 17 of the 30 nodes — the workhorse should
 * recede, and the rare steps should be the ones that catch the eye. Hues are
 * ≥33° apart, and none of them collides with the brand blue (214°) or with the
 * status set in `shared/ui/palette.ts`.
 */
export const CATEGORY_COLOR: Record<NodeCategory, string> = {
  trigger: "violet", // 255° — the entry point
  source: "grape", // 288° — data arriving from an external system
  "data-in": "lime", // 80°  — data typed inline, not fetched
  transform: "teal", // 168° — the calm workhorse
  flow: "indigo", // 230° — control, not data
  // Validate deliberately **shares transform's teal**. The scale is full: cyan
  // already means Confidential (`shared/ui/palette.ts`) and SharePoint, and
  // green/yellow/red are run statuses that appear on the same node. Sharing a
  // hue with transform is the honest reading anyway — validate is a row-shaping
  // step, and what distinguishes it on the canvas is the thing no other node
  // has: two labelled output ports.
  validate: "teal",
  display: "pink", // 330° — terminal, observational
  target: "orange", // 28°  — writes leave the platform
  note: "yellow", // annotations only — never runs, so a status hue is no clash
};

/** The accent for a node, by kind. Unknown kinds fall back to neutral. */
export function nodeColor(kind: string): string {
  const def = NODE_BY_KIND.get(kind);
  return def ? CATEGORY_COLOR[def.category] : "gray";
}

export const CATEGORY_ORDER: NodeCategory[] = [
  "trigger",
  "source",
  "data-in",
  "transform",
  "validate",
  "flow",
  "display",
  "target",
  "note",
];

/** Only a trigger has no input (it's the entry point). A source originates
 *  data but is still wired AFTER the trigger, so it has an input port to be
 *  reachable. A display node produces no output. */
export function hasInput(category: NodeCategory): boolean {
  return category !== "trigger" && category !== "note";
}
export function hasOutput(category: NodeCategory, kind?: string): boolean {
  // Display and Targets are terminal — no output port — with two exceptions:
  // `veeva.sink` emits the vault's per-row load report, and `veeva.staging`
  // emits one row per staged file whose `file` column is what the document
  // loader needs next. Both are targets whose result is data.
  if (kind === "veeva.sink" || kind === "veeva.staging") return true;
  // The archive targets are the same shape: Archive to container and Archive
  // dataset / database emit their per-object archive report, and Ensure
  // archive hands the documents on to the sink after it.
  if (kind?.startsWith("archive.") || kind === "db.snapshot") return true;
  return category !== "display" && category !== "target" && category !== "note";
}

/** How many input ports a node exposes. Triggers: 0. Display: `config.inputs`
 *  (clamped 1–10). Everything else: exactly 1 — one `>` handle = one input, so
 *  wiring can never be ambiguous. */
export function inputPortCount(kind: string, config: Record<string, unknown>): number {
  const def = NODE_BY_KIND.get(kind);
  if (!def || def.category === "trigger" || def.category === "note") return 0;
  if (kind === "display.json") {
    return Math.min(10, Math.max(1, Number(config.inputs ?? 1) || 1));
  }
  // Join, Union and Compare each take two tables → two input ports
  // (top = in-0/left, bottom = in-1/right).
  if (kind === "transform.join" || kind === "transform.union" || kind === "validate.compare")
    return 2;
  // Metadata enrichment: in-0 = the documents, in-1 … = supporting tables.
  if (kind === "ai.predict_metadata") return 1 + supportingInputs(config).length;
  return 1;
}

export const MAX_SUPPORTING_INPUTS = 10;

export interface SupportingInput {
  label: string;
  /** Each row is one valid parent → child combination, columns left to right. */
  hierarchical: boolean;
}

/** Metadata enrichment's supporting inputs, in port order (`in-1` first). */
export function supportingInputs(config: Record<string, unknown>): SupportingInput[] {
  const raw = Array.isArray(config.supporting)
    ? (config.supporting as Partial<SupportingInput>[])
    : [];
  return raw.slice(0, MAX_SUPPORTING_INPUTS).map((s) => ({
    label: String(s?.label ?? ""),
    hierarchical: Boolean(s?.hierarchical),
  }));
}

/** `Input <n>`, plus the label — the name field prompts use for the table. */
export function supportingLabel(input: SupportingInput, index: number): string {
  const label = input.label.trim();
  return label ? `Input ${index} — ${label}` : `Input ${index}`;
}

/** Hover text for an input handle, where the ports mean different things. */
export function inputPortTitle(
  kind: string,
  config: Record<string, unknown>,
  port: number,
): string | undefined {
  if (kind !== "ai.predict_metadata") return undefined;
  if (port === 0) return "Documents";
  const input = supportingInputs(config)[port - 1];
  return input ? supportingLabel(input, port) : undefined;
}

/** Document fields Vault accepts **only** at creation.
 *
 *  Vault reports each as `editable: false` — correct, in that none can be
 *  changed on an existing document — and the mapping picker offers editable
 *  fields, so without this a document create could not be given the type,
 *  subtype or lifecycle it cannot be created without. Hardcoded rather than
 *  derived: every rule that distinguishes them from the forty system-managed
 *  fields beside them (`created_by__v`, `version_id`, …) either caught those
 *  too or caught nothing, and four names from a stable data model beat a
 *  derivation that is wrong in a vault we have not seen. */
export const DOCUMENT_CREATE_FIELDS = [
  "type__v",
  "subtype__v",
  "classification__v",
  "lifecycle__v",
];
